/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import AnimationBaseModel from './base-modeles/animation-base-model';
import _has from 'lodash/has';
import _set from 'lodash/set';
import _get from 'lodash/get';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { observable, decorate } from 'mobx';
import XmlToSvg from '@tencent/xml-to-svg';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IAnimModel, IAnimModelProps, IToStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { nodeTypes, animFillTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import {
    AnimationPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/page/animation-property-converter';
import { AnimType, IEGTimeNodeChoice, ITimingDataModel, PresetId, PresetSubtype, STTLTimeNodePresetClassType, UnitTimeNode } from '@tencent/slide-sdk-model';
import { DeepReadonly } from 'ts-essentials';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';

class AnimationModel extends AnimationBaseModel implements IAnimModel {
    static handleNodeType(sdkData, uiData) {
        if (_has(sdkData, 'nodeType')) {
            const { nodeType } = uiData.props;
            _set(uiData, 'props.nodeType', sdkData.nodeType);
            if (nodeType !== nodeTypes.NONE) {
                if ((nodeType === nodeTypes.CLICK_EFFECT && uiData.props.nodeType !== nodeTypes.CLICK_EFFECT)
                    || (nodeType !== nodeTypes.CLICK_EFFECT && uiData.props.nodeType === nodeTypes.CLICK_EFFECT)) {
                    if (uiData.parent?.timingModel) {
                        uiData.parent.timingModel.resetAnimOrder();
                    }
                }
            }
        }
    }

    static handlePath(sdkData, uiData) {
        if (!_has(sdkData, 'presetClass')) return;
        if (sdkData.presetClass !== 'path') return;
        const [{ path }] = sdkData.childTnLst;
        _set(uiData, 'props.path', path);
    }

    static handlePresetClass(sdkData, uiData) {
        const { presetClass } = sdkData;
        if (!presetClass) return;
        _set(uiData, 'props.presetClass', presetClass);
    }

    static async convertDataSdkToUi(sdkData, uiData, rawUiData) {
        super.convertDataSdkToUi(sdkData, uiData, rawUiData);

        AnimationPropertyConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
    }

    // /动画编辑一期先
    static convertDataUiToSdk(uiData, sdkData, rawUiData) {
        ModelConverterBridge.convertUiToSdk({ type: UIModelType.ANIMATION, uiData, sdkData, rawUiData });
    }
    // id、select 在BaseModel里有定义，直接复用
    public spid: any;
    public order: any;
    public playOrder: number;
    public elementModel: IBaseModel | null;
    public needScroll: boolean;
    public props: IAnimModelProps;
    public audioNumberedSlide?: number;
    private toStyle?: IToStyle;

    constructor() {
        super();

        this.spid = null;                           // /绑定的元素id
        this.order = -1;                            // /动画的click序列
        this.playOrder = -1;                        // 剔除隐藏元素后的动画播放顺序
        this.elementModel = null;                   // /关联元素
        this.needScroll = false;

        this.props = {
            style: {},
            duration: 500,                          // 持续时间，默认500
            delay: 0,                               // 自身的延迟时间，默认为0
            nodeType: nodeTypes.NONE,               // 动画结点动作类型， 默认为单击时
            animType: AnimType.None,               // 动画类型， 目前支持，淡化、缩放、飞入、浮入、出现、脉冲、旋转
            subType: PresetSubtype.None,
            fillType: animFillTypes.HOLD,           // 动画的保持类型， 保持、回退
            by: null,                               // 目前只有强调动画有辅助信息， 所以默认为空，如果有值按后边格式传入 by: {x: 1.5, y: 1.5,}
        };

        decorate(this, {
            props: observable,
        });
    }

    public getShapePathMap() {
        const slide = this.soruceRefForUI;
        const map = new Map();
        const unitTimeNodeMap = (slide?.timingModel.sdkData as ITimingDataModel)?.props.unitTimeNodeMap || {};
        Object.values(unitTimeNodeMap).forEach(seq => seq?.unitTimeNodeList?.
            forEach(unitTimeNodeData => this.handleAnimV3(unitTimeNodeData, map)));
        return map;
    }

    public handleAnimV3(unitTimeNodeData: DeepReadonly<IEGTimeNodeChoice>, map) {
        const { presetClass, id, behaviorList, presetId } = new UnitTimeNode(unitTimeNodeData);
        // NOTE: 曲线向上、向下（Curve Up / Down) 的路径使用 `p:animMotion` 实现，所以此处增加额外判断
        if (presetClass !== STTLTimeNodePresetClassType.path && presetId !== PresetId.Curve) return;
        behaviorList?.forEach((behavior) => {
            const { animMotion } = behavior;
            if (!animMotion) return;
            const path = animMotion?.path;
            const spId = animMotion?.cBhvr?.tgtEl?.spTgt?.spid;
            if (map.has(spId)) {
                map.get(spId).push({ path, id });
            } else {
                map.set(spId, [{ path, id }]);
            }
        });
    }

    public handleAnim(detail, map) {
        const presetClass = detail?.cTn?.presetClass;
        if (presetClass !== 'path') return;
        const id = detail?.cTn?.id;
        detail?.cTn?.childTnLst?.forEach((anim) => {
            if (anim?.type === 'tn_animMotion') {
                const path = anim?.path;
                const spId = _get(anim, 'cBhvr.tgtEl.spTgt.spid');
                if (map.has(spId)) {
                    map.get(spId).push({ path, id });
                } else {
                    map.set(spId, [{ path, id }]);
                }
            }
        });
    }

    public getShapePathList() {
        const shapeId = this.elementModel?.id;
        const map = this.getShapePathMap();
        return map.get(shapeId) || [];
    }

    public getAnimPath(id: string): string {
        if (!this.presentationRef) return '';
        const pathList = this.getShapePathList();
        const [originPath] = pathList.filter(item => item.id === id);
        const originPathStr = originPath?.path;
        const { slideWidth, slideHeight } = this.presentationRef.props;
        const xts = new XmlToSvg(originPathStr).scale(slideWidth, slideHeight)
            .serialize();
        return xts.serializeStr;
    }

    public setAnimModelToStyle(toStyle: IToStyle): void {
        this.toStyle = toStyle;
    }

    public getAnimModelToStyle(): IToStyle | undefined {
        return this.toStyle;
    }
}

export default AnimationModel;
